import React, { useState, useEffect } from 'react';
import MainPlatform from './MainPlatform';
import LandingPage from './LandingPage'; // Maken we in Stap B
import AdminDashboard from './AdminDashboard'; // Admin dashboard
import AccessCodeScreen from './AccessCodeScreen'; // Toegangscode scherm
import PrivacyPage from './PrivacyPage';
import VoorwaardenPage from './VoorwaardenPage';
import AIpolicyPage from './AIpolicyPage';
import CookieverklaringPage from './CookieverklaringPage';
import WerepresenttalentPage from './WerepresenttalentPage';
import WeRepresentPage from './WeRepresentPage';
import UnsubscribePage from './UnsubscribePage';
import UitlegPage from './UitlegPage';
import PublicLanguageDock from './components/PublicLanguageDock';
import { ROUTES } from './domainConfig';
import { useTranslation } from './i18n';

// API URLs - gebruik environment variables voor productie, localhost voor development
const COMPANY_API = process.env.REACT_APP_COMPANY_API || 'http://127.0.0.1:8001';
const CANDIDATE_API = process.env.REACT_APP_CANDIDATE_API || 'http://127.0.0.1:8002';

// Route-key map. De vertaling (en dus browser-tab-titel) wordt runtime bepaald
// via useTranslation zodat een taalwissel direct doorwerkt in de tab.
// In productie zet elke static-build zijn eigen titel via REACT_APP_SEO_TITLE
// (zie render.yaml + index.html), maar lokaal serveren we alle drie de sites
// vanaf één origin, dus moet de titel mee veranderen met de route.
const ROUTE_KEYS = {
  '/': 'root',
  '/talent': 'talent',
  '/bedrijven': 'bedrijven',
  '/business': 'business',
  '/werepresent': 'werepresent',
  '/privacy': 'privacy',
  '/voorwaarden': 'voorwaarden',
  '/ai-policy': 'ai_policy',
  '/cookieverklaring': 'cookieverklaring',
  '/unsubscribe': 'unsubscribe',
  '/uitleg': 'uitleg_candidate',
  '/uitleg-bedrijven': 'uitleg_company',
  '/admin': 'admin',
};

const FALLBACK_TITLE =
  process.env.REACT_APP_SEO_TITLE ||
  'Betaaldsolliciteren.nl - Niet beloven. Bewijzen.';

function pickRouteKey(path) {
  if (path.startsWith('/admin')) return ROUTE_KEYS['/admin'];
  if (path.startsWith('/talent')) return ROUTE_KEYS['/talent'];
  if (path.startsWith('/business')) return ROUTE_KEYS['/business'];
  return ROUTE_KEYS[path] || null;
}

export default function App() {
  const [path, setPath] = useState(window.location.pathname);
  const { t, language } = useTranslation();

  const normalizePath = (target) => {
    try {
      return new URL(target, window.location.origin).pathname;
    } catch (_) {
      return String(target || '/').split('?')[0].split('#')[0] || '/';
    }
  };

  // Toegangscode status - gebruik sessionStorage voor persistentie binnen sessie
  const [accessGranted, setAccessGranted] = useState({
    company: sessionStorage.getItem('access_company') === 'true'
  });

  // Simpele router luisteraar
  useEffect(() => {
    const onPopState = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Houd de browser-tab-titel synchroon met de huidige route én actieve taal.
  useEffect(() => {
    const key = pickRouteKey(path);
    document.title = key ? t(`route_titles.${key}`) : FALLBACK_TITLE;
  }, [path, t, language]);

  const navigate = (newPath) => {
    window.history.pushState({}, "", newPath);
    setPath(normalizePath(newPath));
  };

  // Functie om toegang te verlenen na correcte code
  const grantAccess = (userType) => {
    sessionStorage.setItem(`access_${userType}`, 'true');
    setAccessGranted(prev => ({ ...prev, [userType]: true }));
  };

  // Functie om terug te gaan naar landing (wist niet de toegang)
  const goBack = () => {
    navigate(ROUTES.bedrijvenLanding);
  };

  const renderPublicPage = (page) => (
    <>
      <PublicLanguageDock />
      {page}
    </>
  );

  // ROUTING LOGICA
  
  // 0. ADMIN PORTAL (Poort 8003)
  if (path.startsWith('/admin')) {
    return <AdminDashboard />;
  }
  
  // PRIVACY & VOORWAARDEN PAGINA'S
  if (path === '/privacy') {
    return renderPublicPage(<PrivacyPage onBack={() => navigate('/werepresent')} />);
  }
  
  if (path === '/voorwaarden') {
    return renderPublicPage(<VoorwaardenPage onBack={() => navigate('/werepresent')} onNavigate={navigate} />);
  }
  
  if (path === '/ai-policy') {
    return renderPublicPage(<AIpolicyPage onBack={() => navigate('/werepresent')} />);
  }
  
  if (path === '/cookieverklaring') {
    return renderPublicPage(<CookieverklaringPage onBack={() => navigate('/werepresent')} />);
  }
  
  // UITLEG PAGINA'S
  if (path === '/uitleg') {
    return renderPublicPage(<UitlegPage type="kandidaat" onBack={() => navigate('/')} />);
  }
  
  if (path === '/uitleg-bedrijven') {
    return renderPublicPage(<UitlegPage type="bedrijf" onBack={() => navigate('/bedrijven')} />);
  }
  
  // werepresenttalent landing page (bedrijven)
  if (path === '/bedrijven') {
    return renderPublicPage(<WerepresenttalentPage onNavigate={navigate} />);
  }
  
  // WEREPRESENT PARENT BRAND PAGE
  if (path === '/werepresent') {
    return renderPublicPage(<WeRepresentPage onNavigate={navigate} />);
  }
  
  // EMAIL UNSUBSCRIBE
  if (path === '/unsubscribe') {
    return renderPublicPage(<UnsubscribePage onNavigate={navigate} />);
  }
  
  // 1. BEDRIJVEN PORTAL
  if (path.startsWith('/business')) {
    // Check eerst of toegang is verleend
    if (!accessGranted.company) {
      return (
        <AccessCodeScreen 
          userType="company"
          onSuccess={() => grantAccess('company')}
          onBack={goBack}
        />
      );
    }
    return (
        <MainPlatform 
            userType="company" 
            apiBase={COMPANY_API} 
            onLogout={() => navigate('/')}
            onBackToHome={() => navigate('/')} 
        />
    );
  }
  
  // 2. KANDIDATEN PORTAL
  if (path.startsWith('/talent')) {
    return (
        <MainPlatform 
            userType="candidate" 
            apiBase={CANDIDATE_API} 
            onLogout={() => navigate('/')}
            onBackToHome={() => navigate('/')} 
        />
    );
  }

  // 3. LANDING PAGE (Keuze menu)
  return renderPublicPage(<LandingPage onNavigate={navigate} />);
}